import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Eye, FileText, History, Loader2, RefreshCw, Save, X } from 'lucide-react';
import type { DatenschutzFassung, DatenschutzText } from '../../../services/sifa/types';
import { fetchDatenschutzAktuell, fetchDatenschutzFassungen, speichereDatenschutz } from '../../../services/sifa/datenschutzService';
import {
  DATENSCHUTZ_MUSTER_TEXT,
  DATENSCHUTZ_MUSTER_TITEL,
  fuelleDatenschutz,
  offenePlatzhalter,
} from '../../../services/sifa/datenschutzMuster';
import { describeError } from '../../../services/errorMessage';
import { formatZeit } from '../unterweisungModulLogic';
import { RueckfrageDialog } from './RueckfrageDialog';
import {
  beispielNamen,
  FASSUNGEN_HINWEIS,
  FRAGE_MUSTER_ERSETZT,
  MUSTER_GILT_HINWEIS,
  datenschutzBloecke,
  fassungZeile,
  kannSpeichern,
  musterBrauchtRueckfrage,
  teileMitLuecken,
} from './datenschutzLogic';

interface Props {
  /** Pflegen duerfen nur Admins (RLS Nr. 143) — je Render ausgewertet. */
  isAdmin: boolean;
  isMobile?: boolean;
  /** Ungespeicherte Aenderungen — der Container fragt vor dem Reiterwechsel nach. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Eigener Firmenname (Profil) — Beispiel fuer den Auftragsverarbeiter in der Vorschau. */
  bueroName?: string | null;
}


/**
 * Reiter „Datenschutzhinweise" der Ansicht „Unterweisungen"
 * (docs/sifa-ausbau2-plan.md §0.4): die Hinweise, die Teilnehmer vor der
 * Unterschrift bestaetigen. Admins pflegen sie, alle anderen lesen.
 *
 * Speichern legt IMMER eine neue Fassung an (speichereDatenschutz) — eine
 * Teilnahme verweist auf die Fassung, die bestaetigt wurde, also darf keine
 * sich nachtraeglich aendern. `{{…}}` bleibt im gespeicherten Text; die
 * Vorschau setzt Beispielnamen ein, die Teilnehmerseite die echten.
 */
export const DatenschutzReiter: React.FC<Props> = ({ isAdmin, isMobile = false, onDirtyChange, bueroName }) => {
  const namen = beispielNamen(bueroName);
  const [laedt, setLaedt] = useState(true);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [aktuell, setAktuell] = useState<DatenschutzText | null>(null);
  const [fassungen, setFassungen] = useState<DatenschutzFassung[]>([]);
  const [titel, setTitel] = useState('');
  const [text, setText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [musterFrage, setMusterFrage] = useState(false);
  const [ansicht, setAnsicht] = useState<DatenschutzFassung | null>(null);
  /** Riegel gegen den zweiten Klick — `speichert` steht erst nach dem naechsten Zeichnen. */
  const speichertRef = useRef(false);

  const markiere = (d: boolean) => {
    setDirty(d);
    onDirtyChange?.(d);
  };

  const laden = useCallback(async () => {
    setLaedt(true);
    setLadeFehler(null);
    try {
      const [a, f] = await Promise.all([fetchDatenschutzAktuell(), fetchDatenschutzFassungen()]);
      setAktuell(a);
      setFassungen(f);
      // Der Editor startet mit der geltenden Fassung; ohne eine bleibt er leer
      // („Muster uebernehmen" fuellt ihn ohne Rueckfrage).
      setTitel(a?.titel ?? '');
      setText(a?.text ?? '');
      setDirty(false);
    } catch (e) {
      setLadeFehler(describeError(e));
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  const musterEinsetzen = () => {
    setText(DATENSCHUTZ_MUSTER_TEXT);
    if (!titel.trim()) setTitel(DATENSCHUTZ_MUSTER_TITEL);
    markiere(true);
    setHinweis(null);
    setMusterFrage(false);
  };

  const musterUebernehmen = () => {
    if (musterBrauchtRueckfrage(text)) setMusterFrage(true);
    else musterEinsetzen();
  };

  const speichern = async () => {
    if (!isAdmin || speichertRef.current || !kannSpeichern({ titel, text, dirty, geltend: aktuell })) return;
    speichertRef.current = true;
    setSpeichert(true);
    setFehler(null);
    setHinweis(null);
    try {
      const neu = await speichereDatenschutz(titel, text);
      setAktuell(neu);
      if (neu.id) {
        const gespeichert: DatenschutzFassung = { ...neu, id: neu.id, createdAt: new Date().toISOString() };
        setFassungen((prev) => [gespeichert, ...prev.filter((f) => f.id !== neu.id)]);
      }
      setTitel(neu.titel);
      setText(neu.text);
      markiere(false);
      setHinweis(`Fassung ${neu.fassung} gespeichert. Neue Teilnahmen bestätigen ab jetzt diese Fassung.`);
      // Zeitpunkt und Name stehen erst in der Datenbank — die Liste holt sie nach.
      void fetchDatenschutzFassungen().then(setFassungen).catch(() => undefined);
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      speichertRef.current = false;
      setSpeichert(false);
    }
  };

  const beispielHinweis = `Vorschau mit Beispielnamen: Arbeitgeber „${namen.arbeitgeber}", Auftragsverarbeiter „${namen.auftragsverarbeiter}". Beim Kunden stehen dort die echten Namen.`;

  if (laedt) {
    return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>;
  }

  if (ladeFehler) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex flex-wrap items-center gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0" /><span className="flex-1 min-w-[12rem]">Die Datenschutzhinweise ließen sich nicht laden: {ladeFehler}</span>
        <button onClick={() => void laden()} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-bold text-red-700 bg-white border border-red-200 hover:bg-red-50 rounded-lg">
          <RefreshCw className="w-3.5 h-3.5" /> Erneut laden
        </button>
      </div>
    );
  }

  // ─── Nur lesen ────────────────────────────────────────────────────────────

  if (!isAdmin) {
    const geltend = aktuell ?? { id: null, fassung: 0, titel: DATENSCHUTZ_MUSTER_TITEL, text: DATENSCHUTZ_MUSTER_TEXT };
    return (
      <div className="space-y-3">
        <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
          Nur-Ansicht: Die Datenschutzhinweise pflegen Admins.
          {aktuell ? ` Es gilt Fassung ${aktuell.fassung}.` : ` ${MUSTER_GILT_HINWEIS}`}
        </div>
        <section className="bg-white border border-slate-200 rounded-2xl p-4 md:p-6 space-y-3">
          <p className="text-[11px] text-slate-500">{beispielHinweis}</p>
          <Vorschau titel={geltend.titel} text={geltend.text} namen={namen} />
        </section>
      </div>
    );
  }

  // ─── Pflege (Admins) ──────────────────────────────────────────────────────

  const offen = offenePlatzhalter(text);
  const feld = `w-full px-3 py-2 ${isMobile ? 'min-h-[44px]' : ''} text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-teal-300`;

  return (
    <div className="space-y-4">
      {!aktuell && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-800">
          {MUSTER_GILT_HINWEIS} „Muster übernehmen" setzt es als Ausgangspunkt ein; erst „Speichern" macht daraus Fassung 1.
        </div>
      )}
      {fehler && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2" role="alert">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /><span className="flex-1">{fehler}</span>
          <button onClick={() => setFehler(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {hinweis && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-px" /><span className="flex-1">{hinweis}</span>
          <button onClick={() => setHinweis(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-bold text-slate-800">
              Bearbeiten{aktuell ? <span className="text-xs font-medium text-slate-500"> · geltend: Fassung {aktuell.fassung}</span> : null}
              {dirty ? <span className="text-xs font-medium text-amber-700"> · ungespeichert</span> : null}
            </h2>
            <button onClick={musterUebernehmen} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg">
              <FileText className="w-3.5 h-3.5" /> Muster übernehmen
            </button>
          </div>
          <div>
            <label htmlFor="datenschutz-titel" className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Titel</label>
            <input id="datenschutz-titel" value={titel} onChange={(e) => { setTitel(e.target.value); markiere(true); setHinweis(null); }} maxLength={200} placeholder={DATENSCHUTZ_MUSTER_TITEL} className={feld} />
          </div>
          <div>
            <label htmlFor="datenschutz-text" className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Text</label>
            <textarea
              id="datenschutz-text"
              value={text}
              onChange={(e) => { setText(e.target.value); markiere(true); setHinweis(null); }}
              rows={isMobile ? 16 : 28}
              spellCheck
              className={`${feld} font-mono text-[13px] leading-relaxed`}
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Leerzeile = neuer Absatz, „## " = Überschrift, „- " = Aufzählung. {'{{ARBEITGEBER}}'} und {'{{AUFTRAGSVERARBEITER}}'} setzt die App je Kunde ein; Stellen in [eckigen Klammern] bitte selbst ausfüllen.
            </p>
          </div>
          {offen.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900 space-y-1">
              <p className="font-bold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Noch {offen.length} offene Angabe{offen.length === 1 ? '' : 'n'} — Teilnehmer sehen sie markiert:</p>
              <ul className="list-disc pl-5 space-y-0.5">
                {offen.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </div>
          )}
          <div className="space-y-1">
            <button
              onClick={() => void speichern()}
              disabled={speichert || !kannSpeichern({ titel, text, dirty, geltend: aktuell })}
              className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-xl transition-colors disabled:opacity-40"
            >
              {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Als neue Fassung speichern
            </button>
            <p className="text-[11px] text-slate-500">{FASSUNGEN_HINWEIS}</p>
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <h2 className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Eye className="w-4 h-4 text-slate-500" /> Vorschau</h2>
          <p className="text-[11px] text-slate-500">{beispielHinweis}</p>
          <div data-glass="papier" className="border border-slate-200 rounded-xl p-4 max-h-[70vh] overflow-y-auto">
            {text.trim() ? <Vorschau titel={titel} text={text} namen={namen} /> : <p className="text-xs text-slate-400">Noch kein Text.</p>}
          </div>
        </section>
      </div>

      <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
        <h2 className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><History className="w-4 h-4 text-slate-500" /> Gespeicherte Fassungen <span className="text-xs font-medium text-slate-500">({fassungen.length})</span></h2>
        {fassungen.length === 0 ? (
          <p className="text-xs text-slate-500">Noch keine gespeicherte Fassung — bis dahin gilt das Muster.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {fassungen.map((f) => (
              <li key={f.id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-slate-700">
                  {fassungZeile(f)}
                  {aktuell?.id === f.id && <span className="ml-2 inline-flex px-2 py-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full">geltend</span>}
                </span>
                <button onClick={() => setAnsicht(f)} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg">
                  <Eye className="w-3.5 h-3.5" /> Ansehen
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {musterFrage && (
        <RueckfrageDialog
          titel="Muster übernehmen"
          text={FRAGE_MUSTER_ERSETZT}
          jaText="Text ersetzen"
          gefaehrlich
          onNein={() => setMusterFrage(false)}
          onJa={musterEinsetzen}
        />
      )}

      {ansicht && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setAnsicht(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setAnsicht(null); }}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Fassung ${ansicht.fassung}`}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-5 space-y-3 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-slate-800">Fassung {ansicht.fassung}</h3>
                <p className="text-xs text-slate-500">{ansicht.createdAt ? formatZeit(ansicht.createdAt) : ''}{ansicht.erstelltVonName ? ` · von ${ansicht.erstelltVonName}` : ''}</p>
              </div>
              <button type="button" autoFocus onClick={() => setAnsicht(null)} className="p-2 hover:bg-slate-100 rounded-full" aria-label="Schließen">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <Vorschau titel={ansicht.titel} text={ansicht.text} namen={namen} />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Der Text so, wie ihn Teilnehmer sehen — mit Beispielnamen statt `{{…}}`,
 * offene `[…]` gelb markiert. Bloecke statt HTML: der Text kommt aus einem
 * Eingabefeld und wird nie als Markup eingespeist.
 */
const Vorschau: React.FC<{ titel: string; text: string; namen: { arbeitgeber: string; auftragsverarbeiter: string } }> = ({ titel, text, namen }) => {
  const zeile = (z: string) => teileMitLuecken(z).map((t, i) => (
    t.luecke
      ? <mark key={i} className="bg-amber-100 text-amber-900 rounded px-0.5">{t.text}</mark>
      : <React.Fragment key={i}>{t.text}</React.Fragment>
  ));
  return (
    <article className="space-y-3 text-sm text-slate-700 leading-relaxed">
      <h3 className="text-base font-bold text-slate-800">{(titel || '').trim() || DATENSCHUTZ_MUSTER_TITEL}</h3>
      {datenschutzBloecke(fuelleDatenschutz(text, namen)).map((b, i) => {
        if (b.art === 'ueberschrift') return <h4 key={i} className="text-sm font-bold text-slate-800 pt-2">{zeile(b.text)}</h4>;
        if (b.art === 'liste') {
          return (
            <ul key={i} className="list-disc pl-5 space-y-0.5">
              {b.punkte.map((p, j) => <li key={j}>{zeile(p)}</li>)}
            </ul>
          );
        }
        return (
          <p key={i}>
            {b.zeilen.map((z, j) => <React.Fragment key={j}>{j > 0 && <br />}{zeile(z)}</React.Fragment>)}
          </p>
        );
      })}
    </article>
  );
};
